/* eslint-disable no-param-reassign */
import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiRequestStatus, IApiRequestResult } from './models/apiRequestResult';
import { ILocations, ISubscriptions, LocationType, PollTimeoutType, ResourceKey, StoreApiKeyType } from './models/subscriptions';
import { getConfig } from './configuration/configuration';
import uniq from 'lodash.uniq';

export interface IApiResults extends Record<string, IApiRequestResult> {}

export interface IFrontendStore {
  apiResults: IApiResults;
  subscriptions: ISubscriptions;
  locations: ILocations;
}

export const defaultApiResults: IApiResults = {} as IApiResults;
export const defaultSubscriptions: ISubscriptions = {} as ISubscriptions;
export const defaultLocations: ILocations = {} as ILocations;

export const defaultFrontendStore: IFrontendStore = {
  apiResults: defaultApiResults,
  subscriptions: defaultSubscriptions,
  locations: defaultLocations
};

export const FRONTEND_STORE_NAME = `frontend`;
export const createFrontendStore = (initialState: IFrontendStore) =>
  createSlice({
    name: FRONTEND_STORE_NAME,
    initialState,
    reducers: {
      storeReset: () => defaultFrontendStore,
      apiRequestSent: (store, action: PayloadAction<string>) => {
        const apiRequest = action.payload;
        store.apiResults[apiRequest] = {
          status: ApiRequestStatus.InProgress,
          errorCode: ``
        };
      },
      apiRequestReset: (store, action: PayloadAction<string>) => {
        const apiRequest = action.payload;
        store.apiResults[apiRequest] = {
          status: ApiRequestStatus.NotSubmitted,
          errorCode: ``
        };
      },
      apiRequestSucceeded: (store, action: PayloadAction<string>) => {
        const apiRequest = action.payload;
        store.apiResults[apiRequest] = {
          status: ApiRequestStatus.Success
        };
      },
      apiRequestFailed: (store, action: PayloadAction<{ apiRequest: string; errorCode?: string }>) => {
        const { apiRequest, errorCode } = action.payload;
        store.apiResults[apiRequest] = {
          status: ApiRequestStatus.Failed,
          errorCode
        };
      },
      subscribed: (
        store,
        action: PayloadAction<{
          location: LocationType;
          storeKey: StoreApiKeyType;
          resourceKeys: ResourceKey[];
          pollTimeout?: PollTimeoutType;
        }>
      ) => {
        const configPollTimeout = getConfig().pollTimeout;
        const { location, storeKey, resourceKeys, pollTimeout = configPollTimeout } = action.payload;

        resourceKeys.forEach((key) => {
          const locLocation = (store.locations[location] ??= {});
          const locStoreKey = (locLocation[storeKey] ??= {});
          locStoreKey[key] = pollTimeout;

          const timeouts = uniq([...(store.subscriptions?.[storeKey]?.[key] ?? []), pollTimeout]);
          const subStoreKey = (store.subscriptions[storeKey] ??= {});
          subStoreKey[key] = timeouts;
        });
      },
      unsubscribed: (
        store,
        action: PayloadAction<{
          location: LocationType;
          storeKey: StoreApiKeyType;
          resourceKeys: ResourceKey[];
        }>
      ) => {
        const { location, storeKey, resourceKeys } = action.payload;

        resourceKeys.forEach((resourceKey) => {
          const timeout = store.locations[location]?.[storeKey]?.[resourceKey];
          if (!timeout) {
            return;
          }

          delete store.locations[location][storeKey][resourceKey];

          if (Object.keys(store.locations[location][storeKey]).length === 0) {
            delete store.locations[location][storeKey];
          }

          if (Object.keys(store.locations[location]).length === 0) {
            delete store.locations[location];
          }

          const hasSubscription = Object.values(store.locations).find((loc) => loc[storeKey]?.[resourceKey] === timeout);

          if (!hasSubscription) {
            const timeouts = store.subscriptions[storeKey][resourceKey].filter((t) => t !== timeout);

            if (timeouts.length > 0) {
              store.subscriptions[storeKey][resourceKey] = timeouts;
            } else {
              delete store.subscriptions[storeKey][resourceKey];
            }
          }

          if (Object.keys(store.subscriptions[storeKey]).length === 0) {
            delete store.subscriptions[storeKey];
          }
        });
      }
    }
  });

export const { actions, reducer } = createFrontendStore(defaultFrontendStore);

export const createStore = (initialStore: IFrontendStore = defaultFrontendStore) => {
  const frontendStore = createFrontendStore(initialStore);
  return configureStore({ reducer: { [FRONTEND_STORE_NAME]: frontendStore.reducer } });
};

export const FrontendStore = createStore();

export type IStore = ReturnType<typeof FrontendStore.getState>;
